import React from 'react';

// ROI DISPLAY (Feb 2026)
//
// Regla de negocio: si el activo tiene un Análisis ROI real ejecutado, se
// muestra ese valor en TODA la app (ficha, base de activos, etc.). Si no,
// se muestra el ROI rápido calculado automáticamente, marcado como
// "Estimado".
//
// Marcadores para determinar si es "Real":
//   - ROI Venta   → `asset.tipo_calculo_aplicado` está fijado (solo lo
//                    escribe `POST /assets/{id}/calculate-roi-advanced`).
//                    El valor se lee de `asset.roi_total`.
//   - ROI Alquiler → `asset.rental_analysis.applied_rent_source === 'manual'`
//                     (el usuario introdujo la renta manualmente en la
//                     pestaña ALQUILER). El valor se lee de
//                     `asset.rental_analysis.estimated_rental_roi`.
//
// Colores fijos (nunca modificar):
//   - ROI Venta   → verde (emerald)
//   - ROI Alquiler → naranja (#f97316 / #c2410c)

const COLORS = {
  venta:    { estimado: '#059669', real: '#047857' }, // emerald-600 / emerald-700
  alquiler: { estimado: '#f97316', real: '#c2410c' }, // orange-500 / orange-700
};

export const hasRealRoiVenta = (asset) =>
  Boolean(asset?.tipo_calculo_aplicado) && asset?.roi_total != null;

export const getRoiVentaValue = (asset) =>
  hasRealRoiVenta(asset) ? asset.roi_total : asset?.roi_rapido;

export const hasRealRoiAlquiler = (asset) =>
  asset?.rental_analysis?.applied_rent_source === 'manual' &&
  asset?.rental_analysis?.estimated_rental_roi != null;

export const getRoiAlquilerValue = (asset) =>
  asset?.rental_analysis?.estimated_rental_roi;

const fmt = (v) => `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

/**
 * Muestra un valor de ROI diferenciando "Real" vs "Estimado".
 *  - Estimado: valor + "(Estimado)" en cursiva, en el color base.
 *  - Real: valor + "(Real)" en negrita, con un tono ligeramente más
 *    intenso del mismo color (verde para venta, naranja para alquiler).
 *
 * Props:
 *  - kind: 'venta' | 'alquiler' (por defecto 'venta').
 *  - size: 'lg' | 'sm' (tamaño del valor principal).
 */
export const RoiValue = ({ value, isReal, kind = 'venta', size = 'lg', testid }) => {
  if (value == null || value === undefined || Number.isNaN(Number(value))) {
    return (
      <span
        className={`italic text-slate-400 whitespace-nowrap ${size === 'lg' ? 'text-sm' : 'text-xs'}`}
        title="Falta un dato clave (p.ej. precio de salida) para calcular el ROI con fiabilidad"
        data-testid={testid}
      >
        Sin datos suficientes
      </span>
    );
  }
  const sizeCls = size === 'lg' ? 'text-lg' : 'text-sm';
  const palette = COLORS[kind] || COLORS.venta;
  const color = isReal ? palette.real : palette.estimado;

  if (isReal) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-1.5" data-testid={testid}>
        <span className={`${sizeCls} font-bold`} style={{ color }}>{fmt(value)}</span>
        <span className="text-xs font-bold" style={{ color }}>{kind === 'venta' ? '(Individualizado)' : '(Real)'}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap" data-testid={testid}>
      <span className={`${sizeCls} italic`} style={{ color }}>{fmt(value)}</span>
      <span className="text-xs italic" style={{ color }}>(Estimado)</span>
    </span>
  );
};
